import { BankAccountsEmptyState } from '@/components/bank-accounts/EmptyState';
import {
  TransactionFilters,
  TransactionFiltersProvider,
} from '@/components/transactions/TransactionFilters';
import { useTransactionFilters } from '@/components/transactions/TransactionFilters';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  formatCurrency,
  formatNumberWithCommas,
  parseNumberWithCommas,
} from '@/lib/utils';
import AssetService from '@/services/AssetService';
import TransactionFilterService from '@/services/TransactionFilterService';
import type { TransactionFilter } from '@/services/TransactionFilterService';
import { useQuery } from '@tanstack/react-query';
import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Filter,
  Loader,
  Plus,
  PlusCircle,
  Receipt,
  RefreshCw,
  Search,
  Trash2,
  Unlink,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { toast } from 'sonner';
import BankAccountService, {
  type BankAccount,
  type Transaction,
} from '../services/BankAccountService';

function TransactionTable({ accountId }: { accountId: string }) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [isCreateFilterOpen, setIsCreateFilterOpen] = useState(false);
  const [editingFilter, setEditingFilter] = useState<TransactionFilter | null>(
    null,
  );
  const [newFilter, setNewFilter] = useState<{
    name: string;
    description_pattern: string;
    amount_condition: 'equals' | 'greater_than' | 'less_than';
    amount_value: number;
    transaction_type: 'INCOME' | 'EXPENSE';
    splits: Array<{ assetId: string; percentage: number }>;
  }>({
    name: '',
    description_pattern: '',
    amount_condition: 'equals',
    amount_value: 0,
    transaction_type: 'EXPENSE',
    splits: [{ assetId: '', percentage: 100 }],
  });

  const { data: transactions, isLoading } = useQuery({
    queryKey: ['transactions', accountId],
    queryFn: () => BankAccountService.getAccountTransactions(accountId),
  });

  const { data: assets } = useQuery({
    queryKey: ['assets'],
    queryFn: () => AssetService.getAssets(),
  });

  const { getMatchingFilter, refetchFilters } = useTransactionFilters();

  const handleCreateFilter = (transaction: Transaction) => {
    const existingFilter = getMatchingFilter(transaction);
    if (existingFilter) {
      setEditingFilter(existingFilter);
      setNewFilter({
        name: existingFilter.name,
        description_pattern: existingFilter.description_pattern,
        amount_condition: existingFilter.amount_condition,
        amount_value: existingFilter.amount_value,
        transaction_type: existingFilter.transaction_type,
        splits: existingFilter.splits,
      });
    } else {
      setEditingFilter(null);
      setNewFilter({
        name: `${transaction.name} Filter`,
        description_pattern: transaction.name,
        amount_condition: 'equals',
        amount_value: transaction.amount,
        transaction_type: transaction.transaction_type,
        splits: [{ assetId: '', percentage: 100 }],
      });
    }
    setIsCreateFilterOpen(true);
  };

  const handleAddSplit = () => {
    setNewFilter((prev) => ({
      ...prev,
      splits: [...prev.splits, { assetId: '', percentage: 0 }],
    }));
  };

  const handleRemoveSplit = (index: number) => {
    setNewFilter((prev) => ({
      ...prev,
      splits: prev.splits.filter((_, i) => i !== index),
    }));
  };

  const handleSplitChange = (
    index: number,
    field: keyof { assetId: string; percentage: number },
    value: string | number,
  ) => {
    setNewFilter((prev) => ({
      ...prev,
      splits: prev.splits.map((split, i) =>
        i === index ? { ...split, [field]: value } : split,
      ),
    }));
  };

  const handleSaveFilter = async () => {
    // Validate required fields
    if (!newFilter.name.trim()) {
      toast.error('Filter name is required');
      return;
    }

    if (!newFilter.description_pattern.trim()) {
      toast.error('Description pattern is required');
      return;
    }

    // Ensure amount_value is a number
    const amountValue = Number(newFilter.amount_value);
    if (Number.isNaN(amountValue) || amountValue <= 0) {
      toast.error('Amount value must be a valid number greater than 0');
      return;
    }

    // Validate splits
    if (!newFilter.splits.length) {
      toast.error('At least one split is required');
      return;
    }

    // Validate total percentage equals 100
    const totalPercentage = newFilter.splits.reduce(
      (sum, split) => sum + split.percentage,
      0,
    );

    if (Math.abs(totalPercentage - 100) > 0.01) {
      // Allow for small floating point differences
      toast.error('Total percentage must equal 100%');
      return;
    }

    // Validate all splits have an asset selected
    const hasInvalidSplits = newFilter.splits.some((split) => !split.assetId);
    if (hasInvalidSplits) {
      toast.error('All splits must have an asset selected');
      return;
    }

    try {
      if (editingFilter) {
        await TransactionFilterService.updateFilter(editingFilter.id, {
          ...newFilter,
          amount_value: amountValue, // Use the parsed number
        });
        toast.success('Filter updated successfully');
      } else {
        await TransactionFilterService.createFilter({
          ...newFilter,
          amount_value: amountValue, // Use the parsed number
        });
        toast.success('Filter created successfully');
      }
      setIsCreateFilterOpen(false);
      refetchFilters();
    } catch (error) {
      console.error('Error saving filter:', error);
      toast.error(
        editingFilter ? 'Failed to update filter' : 'Failed to create filter',
      );
    }
  };

  const columns: ColumnDef<Transaction>[] = [
    {
      accessorKey: 'date',
      header: 'Date',
      cell: ({ row }) => new Date(row.getValue('date')).toLocaleDateString(),
    },
    {
      accessorKey: 'name',
      header: 'Description',
    },
    {
      accessorKey: 'category',
      header: 'Category',
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: ({ row }) => {
        const amount = row.getValue('amount') as number;
        const type = row.original.transaction_type;
        return (
          <span
            className={`font-medium ${
              type === 'INCOME' ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {type === 'INCOME' ? '+' : '-'}
            {formatCurrency(amount)}
          </span>
        );
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const transaction = row.original;
        const matchingFilter = getMatchingFilter(transaction);
        return (
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              variant={matchingFilter ? 'default' : 'ghost'}
              size="icon"
              onClick={() => handleCreateFilter(transaction)}
            >
              <Filter className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: transactions || [],
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!transactions?.length) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No transactions found
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search transactions..."
            value={globalFilter ?? ''}
            onChange={(event) => setGlobalFilter(event.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className="group"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-end space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          Next
        </Button>
      </div>

      <Dialog open={isCreateFilterOpen} onOpenChange={setIsCreateFilterOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              {editingFilter
                ? 'Update Transaction Filter'
                : 'Create Transaction Filter'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="filter-name">
                Filter Name
              </label>
              <Input
                id="filter-name"
                value={newFilter.name}
                onChange={(e) =>
                  setNewFilter((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="e.g., Mortgage Payments"
              />
            </div>

            <div className="space-y-2">
              <label
                className="text-sm font-medium"
                htmlFor="description-pattern"
              >
                Description Pattern
              </label>
              <Input
                id="description-pattern"
                value={newFilter.description_pattern}
                onChange={(e) =>
                  setNewFilter((prev) => ({
                    ...prev,
                    description_pattern: e.target.value,
                  }))
                }
                placeholder="e.g., MORTGAGE PAYMENT"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label
                  className="text-sm font-medium"
                  htmlFor="amount-condition"
                >
                  Amount Condition
                </label>
                <Select
                  value={newFilter.amount_condition}
                  onValueChange={(
                    value: 'equals' | 'greater_than' | 'less_than',
                  ) =>
                    setNewFilter((prev) => ({
                      ...prev,
                      amount_condition: value,
                    }))
                  }
                >
                  <SelectTrigger id="amount-condition">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="equals">Equals</SelectItem>
                    <SelectItem value="greater_than">Greater Than</SelectItem>
                    <SelectItem value="less_than">Less Than</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="amount-value">
                  Amount Value
                </label>
                <div className="relative">
                  <Input
                    id="amount-value"
                    type="text"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    className="pl-8"
                    value={formatNumberWithCommas(newFilter.amount_value)}
                    onChange={(e) => {
                      const value = parseNumberWithCommas(e.target.value);
                      setNewFilter((prev) => ({
                        ...prev,
                        amount_value: value || 0,
                      }));
                    }}
                  />
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    $
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="transaction-type">
                Transaction Type
              </label>
              <Select
                value={newFilter.transaction_type}
                onValueChange={(value: 'INCOME' | 'EXPENSE') =>
                  setNewFilter((prev) => ({
                    ...prev,
                    transaction_type: value,
                  }))
                }
              >
                <SelectTrigger id="transaction-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="INCOME">Income</SelectItem>
                  <SelectItem value="EXPENSE">Expense</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label
                  className="text-sm font-medium"
                  htmlFor="split-between-assets"
                >
                  Split Between Assets
                </label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddSplit}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Split
                </Button>
              </div>
              {newFilter.splits.map((split, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <Select
                    value={split.assetId}
                    onValueChange={(value) =>
                      handleSplitChange(index, 'assetId', value)
                    }
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select asset" />
                    </SelectTrigger>
                    <SelectContent>
                      {assets?.map((asset) => (
                        <SelectItem key={asset.id} value={asset.id}>
                          {asset.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    className="w-24"
                    min="0"
                    max="100"
                    step="0.01"
                    value={split.percentage}
                    onChange={(e) =>
                      handleSplitChange(
                        index,
                        'percentage',
                        Number.parseFloat(e.target.value) || 0,
                      )
                    }
                    placeholder="%"
                  />
                  {index > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveSplit(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateFilterOpen(false);
                setEditingFilter(null);
                setNewFilter({
                  name: '',
                  description_pattern: '',
                  amount_condition: 'equals',
                  amount_value: 0,
                  transaction_type: 'EXPENSE',
                  splits: [{ assetId: '', percentage: 100 }],
                });
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveFilter}>
              {editingFilter ? 'Update Filter' : 'Save Filter'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function BankAccountsPage() {
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [balances, setBalances] = useState<
    Record<string, { current: number; available: number | null }>
  >({});

  // Fetch link token when component mounts
  useEffect(() => {
    const fetchLinkToken = async () => {
      try {
        const response = await BankAccountService.createLinkToken();
        setLinkToken(response.link_token);
      } catch (error) {
        console.error('Error fetching link token:', error);
        toast.error('Failed to initialize bank connection');
      }
    };
    fetchLinkToken();
  }, []);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: async (publicToken, metadata) => {
      try {
        const linkedAccounts = await BankAccountService.linkBankAccount({
          publicToken,
        });

        toast.success('Bank account connected successfully');
        setAccounts((prev) => [...prev, ...linkedAccounts]);

        for (const account of linkedAccounts) {
          fetchAccountBalance(account.id.toString());
        }

        // Refresh link token for next connection
        const response = await BankAccountService.createLinkToken();
        setLinkToken(response.link_token);
      } catch (error) {
        console.error('Error connecting bank account:', error);
        toast.error('Failed to connect bank account');
      }
    },
    onExit: (err, metadata) => {
      if (err) {
        console.error('Plaid Link error:', err);
      } else {
        console.log('Plaid Link closed');
      }
    },
    onEvent: (eventName, metadata) => {
      console.log('Plaid Link event:', eventName, metadata);
    },
  });

  const fetchAccountBalance = useCallback(async (accountId: string) => {
    try {
      const balanceData = await BankAccountService.getAccountBalance(accountId);
      setBalances((prev) => ({
        ...prev,
        [accountId]: balanceData.balance,
      }));
    } catch (error: unknown) {
      console.error(`Error fetching balance for account ${accountId}:`, error);

      // Handle reconnection cases
      if (
        error &&
        typeof error === 'object' &&
        'response' in error &&
        error.response &&
        typeof error.response === 'object' &&
        'status' in error.response &&
        (error.response.status === 401 || error.response.status === 400)
      ) {
        setBalances((prev) => ({
          ...prev,
          [accountId]: { current: 0, available: null },
        }));

        // Show reconnection message
        toast.error(
          error.response &&
            typeof error.response === 'object' &&
            'data' in error.response
            ? (error.response.data as { message?: string })?.message ||
                'Please reconnect your bank account'
            : 'Please reconnect your bank account',
        );
      }
    }
  }, []);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await BankAccountService.getBankAccounts();
      setAccounts(data);

      // Fetch balances for all accounts
      for (const account of data) {
        fetchAccountBalance(account.id.toString());
      }
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
      toast.error('Failed to load bank accounts');
    } finally {
      setLoading(false);
    }
  }, [fetchAccountBalance]);

  const syncTransactions = async (accountId: string) => {
    setSyncing(accountId);
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const result = await BankAccountService.syncTransactions({
        accountId,
        from: thirtyDaysAgo.toISOString(),
        to: new Date().toISOString(),
      });

      toast.success(`Synced ${result.count} transactions successfully`);

      // Refresh accounts to update last_fetched timestamp
      fetchAccounts();
    } catch (error: unknown) {
      console.error('Error syncing transactions:', error);

      // Handle reconnection cases
      if (
        error &&
        typeof error === 'object' &&
        'response' in error &&
        error.response &&
        typeof error.response === 'object' &&
        'status' in error.response &&
        (error.response.status === 401 || error.response.status === 400)
      ) {
        toast.error(
          error.response &&
            typeof error.response === 'object' &&
            'data' in error.response
            ? (error.response.data as { message?: string })?.message ||
                'Please reconnect your bank account'
            : 'Please reconnect your bank account',
        );

        // Refresh accounts to update status
        fetchAccounts();
      } else {
        toast.error('Failed to sync transactions');
      }
    } finally {
      setSyncing(null);
    }
  };

  const unlinkAccount = async (accountId: string) => {
    try {
      await BankAccountService.unlinkAccount(accountId);
      setAccounts((prev) =>
        prev.filter((acc) => acc.id.toString() !== accountId),
      );
      toast.success('Account unlinked successfully');
    } catch (error) {
      console.error('Error unlinking account:', error);
      toast.error('Failed to unlink account');
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  if (loading) {
    return (
      <div className="container mx-auto py-8 flex justify-center items-center min-h-[400px]">
        <Loader className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <TransactionFiltersProvider>
        <Tabs defaultValue="accounts" className="space-y-4">
          <TabsList>
            <TabsTrigger value="accounts">Accounts</TabsTrigger>
            <TabsTrigger value="filters">Filters</TabsTrigger>
          </TabsList>

          <TabsContent value="accounts" className="space-y-4">
            <div className="flex justify-end mb-6">
              <Button onClick={() => open()} disabled={!ready || !linkToken}>
                <PlusCircle className="h-4 w-4 mr-2" />
                Connect Bank Account
              </Button>
            </div>

            <div className="grid gap-4">
              {accounts.map((account) => (
                <Card key={account.id}>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">
                      {account.institution_name} - {account.account_name}
                    </CardTitle>
                    <div className="flex gap-2">
                      <Sheet>
                        <SheetTrigger asChild>
                          <Button variant="outline" size="sm">
                            <Receipt className="h-4 w-4 mr-2" />
                            Transactions
                          </Button>
                        </SheetTrigger>
                        <SheetContent
                          className="w-[90vw] max-w-[1200px] sm:max-w-[1200px] !w-[90vw]"
                          style={{ width: '90vw', maxWidth: '1200px' }}
                        >
                          <SheetHeader>
                            <SheetTitle>
                              Transactions - {account.institution_name} -{' '}
                              {account.account_name}
                            </SheetTitle>
                          </SheetHeader>
                          <div className="mt-6">
                            <TransactionTable accountId={account.id} />
                          </div>
                        </SheetContent>
                      </Sheet>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => syncTransactions(account.id.toString())}
                        disabled={syncing === account.id.toString()}
                      >
                        {syncing === account.id.toString() ? (
                          <Loader className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                        <span className="ml-2">Sync</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => unlinkAccount(account.id.toString())}
                      >
                        <Unlink className="h-4 w-4" />
                        <span className="ml-2">Unlink</span>
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Institution
                        </p>
                        <p className="font-medium">
                          {account.institution_name}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Account Name
                        </p>
                        <p className="font-medium">{account.account_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Balance</p>
                        <p className="font-medium">
                          {balances[account.id] ? (
                            balances[account.id].current === null ? (
                              <span className="text-yellow-600">
                                Reconnection needed
                              </span>
                            ) : (
                              new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD',
                              }).format(balances[account.id].current)
                            )
                          ) : (
                            <span className="text-muted-foreground">
                              Loading...
                            </span>
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Last Synced
                        </p>
                        <p className="font-medium">
                          {account.last_fetched
                            ? new Date(account.last_fetched).toLocaleString()
                            : 'Never'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {accounts.length === 0 && (
                <div className="flex justify-center">
                  <BankAccountsEmptyState onConnectAccount={() => open()} />
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="filters" className="space-y-4">
            <TransactionFilters />
          </TabsContent>
        </Tabs>
      </TransactionFiltersProvider>
    </div>
  );
}
